import { default as React, useMemo, useRef, useState } from 'react';

import { htmlEditButton } from 'quill-html-edit-button';
import ReactQuill, { Quill } from 'react-quill';
import Select from 'react-select';
import sanitizeHtml from 'sanitize-html';

import Button from 'CommonComponents/Button';
import Toast from 'CommonComponents/Toast';

import 'react-quill/dist/quill.snow.css';

Quill.register('modules/htmlEditButton', htmlEditButton, true);

export default function AdminInstructionForm(props) {
    const [section, setSection] = useState(props.section);
    const [sections, setSections] = useState(props.sections);
    const [sectionId, setSectionId] = useState(props.sectionId);
    const [sectionTouched, setSectionTouched] = useState(false);
    const [content, setContent] = useState(props.content);
    const [order, setOrder] = useState(props.order);
    const [request, setRequest] = useState(false);

    const [message, setMessage] = useState('');
    const [toastId, setToastId] = useState('instruction-updated');

    const [selectParentSectionOptions, setSelectParentSectionOptions] = useState([]);
    const [selectParentSection, setSelectParentSection] = useState(null);

    useEffect(() => {
        if (sections) {
            let options = [];
            sections.forEach(function (parentSection) {
                options.push({
                    value: parentSection.id,
                    label: parentSection.section,
                });
            });
            setSelectParentSectionOptions(options);
        }
    }, [sections]);

    useEffect(() => {
        if (sections) {
            var selectedOption = null;
            sections.forEach(function (parentSection) {
                if (sectionId == parentSection.id) {
                    selectedOption = { value: parentSection.id, label: parentSection.section };
                }
            });
            setSelectParentSection(selectedOption);
        }
    }, [selectParentSectionOptions]);

    const quillRef = useRef();

    async function imageHandler() {
        const editor = quillRef.current.getEditor();

        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (/^image\//.test(file.type)) {
                setRequest(true);
                const formData = new FormData();
                formData.append('image', file);

                await axios({
                    url: props.uploadUrl,
                    method: 'post',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                    .then((response) => {
                        const res = response.data.url;
                        editor.insertEmbed(editor.getSelection(), 'image', res);
                    })
                    .catch((e) => {
                        console.log(e);
                    });

                setRequest(false);
            } else {
                console.log('You could only upload images.');
            }
        };
    }

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                    [{ size: [] }],
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['link', 'image', 'video'],
                    ['clean'],
                    [{ color: [] }],
                ],
                handlers: {
                    image: imageHandler,
                },
            },
            htmlEditButton: {},
        }),
        [],
    );

    const changeSectionName = (e) => {
        setSection(e.target.value);
    };

    const changeOrder = (e) => {
        const value = e.target.value;
        if (!isNaN(value)) {
            setOrder(value);
        }
    };

    const sanitizeOptions = {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'video', 'iframe', 'h1', 'h2', 'span']),
        allowedAttributes: {
            ...sanitizeHtml.defaults.allowedAttributes,
            img: ['src', 'alt', 'width', 'height'],
            video: ['src', 'controls', 'width', 'height'],
            iframe: ['src', 'width', 'height', 'frameborder', 'allowfullscreen'],
            '*': ['class', 'style'],
        },
        allowedStyles: {
            '*': {
                color: [/^.*$/],
                'text-align': [/^.*$/],
            },
        },
        // Restrict iframe embeds to match the backend URI.SafeIframeRegexp exactly:
        // www.youtube.com/embed/ and player.vimeo.com/video/ only.
        allowedIframeHostnames: ['www.youtube.com', 'player.vimeo.com'],
    };

    const handleSubmit = () => {
        const data = {
            content: sanitizeHtml(content, sanitizeOptions),
            section,
            order,
            section_id: selectParentSection?.value,
        };
        setRequest(true);
        axios
            .post(saveUrl, data)
            .then((response) => {
                if (props.redirectUrl) {
                    const url = props.redirectUrl.replace('INSTRUCTION_ID', response.data.instruction.id);
                    window.location.href = url;
                }

                setSection(response.data.instruction.section);
                setContent(response.data.instruction.content);
                setOrder(response.data.instruction.order);

                setMessage($t('Section has been successfully updated'));
                setTimeout(() => {
                    $(`#${toastId}`).toast('show');
                }, 0);
            })
            .catch((e) => {
                setMessage($t('Something went wrong'));
                setTimeout(() => {
                    $(`#${toastId}`).toast('show');
                }, 0);
            })
            .finally(() => {
                setRequest(false);
            });
    };

    function onChangeSelectParentSection(option) {
        setSelectParentSection(option);
    }

    return (
        <div>
            <div className="form-row my-4">
                <div className="col-md-6 form-group">
                    <label htmlFor="sectionName">
                        {$t('Section')} <span className="text-danger">*</span>
                    </label>
                    <input
                        id="sectionName"
                        type="text"
                        className={!section && sectionTouched ? 'form-control is-invalid border border-danger' : 'form-control'}
                        name="section"
                        placeholder={$t('Section Name')}
                        value={section}
                        onChange={changeSectionName}
                        onBlur={() => {
                            setSectionTouched(true);
                        }}
                    />
                    {!section && sectionTouched && <div className="text-danger">{$t('This field is required')}</div>}
                </div>
                <div className="col-md-6 form-group">
                    <label>{$t('Parent Section')}</label>
                    <Select
                        placeholder={$t('Select parent section')}
                        options={selectParentSectionOptions}
                        value={selectParentSection}
                        onChange={onChangeSelectParentSection}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="col-12">
                    <ReactQuill ref={quillRef} modules={modules} value={content} onChange={setContent} />
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-12 d-flex justify-content-end">
                    <Button variant="primary" disabled={!section || request} onClick={handleSubmit}>
                        {$t('Save')}
                    </Button>
                </div>
            </div>
            <Toast id={toastId} message={message}></Toast>
        </div>
    );
}
